import React, { useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Layout from '../../components/Layout/Layout';
import Seo from '../../components/Seo';

const NotFoundPage = () => {
    const { lang: urlLang } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { i18n } = useTranslation();

    // Determine current language: if urlLang is 'en' or 'ka', use it; otherwise check i18n or default to 'en'
    const currentLang = (urlLang === 'en' || urlLang === 'ka') 
        ? urlLang 
        : (i18n.language === 'ka' ? 'ka' : 'en');

    useEffect(() => {
        if (currentLang && (currentLang === 'en' || currentLang === 'ka')) {
            i18n.changeLanguage(currentLang);
        }
        window.scrollTo(0, 0);
    }, [currentLang, i18n]);

    const isKa = currentLang === 'ka';

    return (
        <Layout>
            <Seo 
                title={isKa ? '404 - გვერდი ვერ მოიძებნა | Orbitronix' : '404 - Lost in Orbit | Orbitronix'}
                description={isKa ? 'მოთხოვნილი გვერდი არ არსებობს ან გადატანილია.' : 'The coordinate you requested does not exist or has drifted off-course.'}
                url={`/${currentLang}/404`}
            />

            <main className="page_content nf-page-wrapper">
                {/* Ambient Cosmic Lights */}
                <div className="nf-ambient-light light-1" />
                <div className="nf-ambient-light light-2" />

                <div className="container py-160">
                    <div className="row justify-content-center text-center">
                        <div className="col-lg-9 col-xl-8">
                            
                            {/* Orbit Lost Badge */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                className="d-inline-flex align-items-center gap-2 nf-status-badge mb-4"
                            >
                                <span className="nf-pulse-radar" />
                                <span>{isKa ? 'ორბიტალური სიგნალი შეწყვეტილია • შეცდომა 404' : 'ORBIT SIGNAL DISCONNECTED • ERROR 404'}</span>
                            </motion.div>

                            {/* 3D Holographic 404 Visual */}
                            <motion.div 
                                className="nf-visual-stage"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                            >
                                <div className="nf-orbit-ring ring-1" />
                                <div className="nf-orbit-ring ring-2" />
                                <div className="nf-floating-satellite">
                                    <span>🛰️</span>
                                </div>
                                <h1 className="nf-giant-code">404</h1>
                            </motion.div>

                            {/* Heading & Explanation */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.25 }}
                            >
                                <h2 className="nf-heading mb-3">
                                    {isKa ? 'გვერდი ორბიტაზე არ მოიძებნა' : 'Lost in Digital Orbit'}
                                </h2>
                                
                                <p className="nf-desc mx-auto mb-4">
                                    {isKa 
                                        ? 'თქვენს მიერ მოთხოვნილი მისამართი არ არსებობს, შეცვლილია ან დროებით გათიშულია. დაბრუნდით მთავარზე ან ისარგებლეთ ქვემოთ მოცემული ბმულებით.'
                                        : 'The coordinate you entered does not exist or has moved out of reach. Check the URL for typos, return to base, or navigate using the navigation hub.'}
                                </p>

                                <div className="nf-path-trace mb-4">
                                    <code>PATH: {location.pathname}</code>
                                </div>

                                {/* Primary Actions */}
                                <div className="nf-actions-row mb-60">
                                    <Link to={`/${currentLang}`} className="nf-btn-primary">
                                        <i className="fas fa-home me-2" />
                                        <span>{isKa ? 'მთავარზე დაბრუნება' : 'Return to Base'}</span>
                                    </Link>
                                    
                                    <Link to={`/${currentLang}/products`} className="nf-btn-secondary">
                                        <i className="fas fa-boxes me-2" />
                                        <span>{isKa ? 'პროდუქტების კატალოგი' : 'Explore Products'}</span>
                                    </Link>

                                    <button onClick={() => navigate(-1)} className="nf-btn-ghost">
                                        <i className="fas fa-arrow-left me-2" />
                                        <span>{isKa ? 'უკან' : 'Go Back'}</span>
                                    </button>
                                </div>
                            </motion.div>

                            {/* Quick Navigation Hub */}
                            <motion.div 
                                className="nf-nav-hub"
                                initial={{ opacity: 0, y: 30 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                <div className="nf-hub-title mb-4">
                                    <i className="fas fa-compass me-2 text-primary" />
                                    <span>{isKa ? 'სწრაფი ნავიგაციის ცენტრი' : 'Quick Navigation Hub'}</span>
                                </div>

                                <div className="row g-3 text-start">
                                    <div className="col-sm-6 col-md-3">
                                        <Link to={`/${currentLang}/products`} className="nf-hub-card">
                                            <span className="hub-icon">📦</span>
                                            <span className="hub-label">{isKa ? 'პროდუქტები' : 'Products'}</span>
                                            <small>{isKa ? 'მზა პლატფორმები' : 'Software catalog'}</small>
                                        </Link>
                                    </div>

                                    <div className="col-sm-6 col-md-3">
                                        <Link to={`/${currentLang}/services`} className="nf-hub-card">
                                            <span className="hub-icon">⚙️</span>
                                            <span className="hub-label">{isKa ? 'სერვისები' : 'Services'}</span>
                                            <small>{isKa ? 'ვებ & მობილური' : 'Custom engineering'}</small>
                                        </Link>
                                    </div>

                                    <div className="col-sm-6 col-md-3">
                                        <Link to={`/${currentLang}/blog`} className="nf-hub-card">
                                            <span className="hub-icon">📰</span>
                                            <span className="hub-label">{isKa ? 'ბლოგი & AI' : 'Insights'}</span>
                                            <small>{isKa ? 'კვლევები & სიახლე' : 'Tech research'}</small>
                                        </Link>
                                    </div>

                                    <div className="col-sm-6 col-md-3">
                                        <Link to={`/${currentLang}/contact`} className="nf-hub-card">
                                            <span className="hub-icon">💬</span>
                                            <span className="hub-label">{isKa ? 'კონტაქტი' : 'Contact'}</span>
                                            <small>{isKa ? 'დაგვიკავშირდით' : 'Support desk'}</small>
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>

                        </div>
                    </div>
                </div>
            </main>

            <style>{`
                .nf-page-wrapper {
                    position: relative;
                    min-height: 85vh;
                    background: #060918;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                }

                .nf-ambient-light {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(100px);
                    pointer-events: none;
                    opacity: 0.35;
                }
                .light-1 {
                    top: 15%;
                    left: 20%;
                    width: 480px;
                    height: 480px;
                    background: radial-gradient(circle, #6c5ce7 0%, transparent 70%);
                }
                .light-2 {
                    bottom: 10%;
                    right: 20%;
                    width: 520px;
                    height: 520px;
                    background: radial-gradient(circle, #38bdf8 0%, transparent 70%);
                }

                .nf-status-badge {
                    background: rgba(244, 63, 94, 0.12);
                    border: 1px solid rgba(244, 63, 94, 0.35);
                    color: #fda4af;
                    font-size: 11.5px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    padding: 6px 16px;
                    border-radius: 30px;
                }
                .nf-pulse-radar {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: #f43f5e;
                    box-shadow: 0 0 10px #f43f5e;
                    animation: nf-radar 1.5s infinite;
                }

                /* 3D Visual Stage */
                .nf-visual-stage {
                    position: relative;
                    width: 320px;
                    height: 180px;
                    margin: 0 auto 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .nf-giant-code {
                    font-size: clamp(6.5rem, 16vw, 11rem);
                    font-weight: 900;
                    line-height: 1;
                    margin: 0;
                    letter-spacing: -6px;
                    background: linear-gradient(180deg, #ffffff 0%, #cbd5e1 45%, #64748b 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    text-shadow: 0 15px 40px rgba(0, 0, 0, 0.8);
                    position: relative;
                    z-index: 2;
                }
                .nf-orbit-ring {
                    position: absolute;
                    border-radius: 50%;
                    border: 1px dashed rgba(108, 92, 231, 0.4);
                }
                .ring-1 {
                    width: 290px;
                    height: 140px;
                    transform: rotate(-25deg);
                    animation: nf-spin 30s linear infinite;
                }
                .ring-2 {
                    width: 330px;
                    height: 160px;
                    transform: rotate(20deg);
                    border-color: rgba(56, 189, 248, 0.3);
                    animation: nf-spin-reverse 35s linear infinite;
                }
                .nf-floating-satellite {
                    position: absolute;
                    top: 10px;
                    right: 25px;
                    font-size: 26px;
                    z-index: 3;
                    animation: nf-float 4s ease-in-out infinite;
                }

                .nf-heading {
                    color: #ffffff;
                    font-size: clamp(1.8rem, 3.8vw, 2.6rem);
                    font-weight: 800;
                    letter-spacing: -0.02em;
                }
                .nf-desc {
                    color: rgba(255, 255, 255, 0.65);
                    font-size: 16px;
                    line-height: 1.7;
                    max-width: 580px;
                }

                .nf-path-trace {
                    display: inline-block;
                    background: rgba(0, 0, 0, 0.4);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 4px 14px;
                    border-radius: 8px;
                }
                .nf-path-trace code {
                    color: #94a3b8;
                    font-size: 12px;
                    font-family: monospace;
                }

                /* Action buttons */
                .nf-actions-row {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 12px;
                }
                .nf-btn-primary {
                    display: inline-flex;
                    align-items: center;
                    padding: 13px 26px;
                    border-radius: 30px;
                    background: linear-gradient(135deg, #6c5ce7, #4f46e5);
                    color: #fff;
                    font-weight: 700;
                    font-size: 14px;
                    text-decoration: none;
                    transition: all 0.25s ease;
                    box-shadow: 0 4px 20px rgba(108, 92, 231, 0.4);
                }
                .nf-btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 25px rgba(108, 92, 231, 0.55);
                    color: #fff;
                }
                .nf-btn-secondary {
                    display: inline-flex;
                    align-items: center;
                    padding: 13px 24px;
                    border-radius: 30px;
                    background: rgba(255, 255, 255, 0.06);
                    border: 1px solid rgba(255, 255, 255, 0.15);
                    color: #fff;
                    font-weight: 700;
                    font-size: 14px;
                    text-decoration: none;
                    transition: all 0.25s ease;
                }
                .nf-btn-secondary:hover {
                    background: rgba(255, 255, 255, 0.12);
                    border-color: rgba(255, 255, 255, 0.3);
                    color: #fff;
                    transform: translateY(-2px);
                }
                .nf-btn-ghost {
                    display: inline-flex;
                    align-items: center;
                    padding: 13px 20px;
                    border-radius: 30px;
                    background: transparent;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.7);
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.25s ease;
                }
                .nf-btn-ghost:hover {
                    color: #fff;
                    border-color: rgba(255, 255, 255, 0.25);
                }

                /* Navigation Hub */
                .nf-nav-hub {
                    background: rgba(14, 20, 48, 0.65);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 24px;
                    padding: 28px 24px;
                    backdrop-filter: blur(14px);
                }
                .nf-hub-title {
                    font-size: 12.5px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    color: rgba(255, 255, 255, 0.7);
                    text-transform: uppercase;
                }
                .nf-hub-card {
                    display: flex;
                    flex-direction: column;
                    padding: 18px 16px;
                    border-radius: 16px;
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.06);
                    text-decoration: none;
                    transition: all 0.25s ease;
                }
                .nf-hub-card:hover {
                    background: rgba(108, 92, 231, 0.15);
                    border-color: rgba(108, 92, 231, 0.4);
                    transform: translateY(-3px);
                }
                .hub-icon {
                    font-size: 24px;
                    margin-bottom: 8px;
                }
                .hub-label {
                    font-weight: 700;
                    color: #fff;
                    font-size: 14px;
                    margin-bottom: 2px;
                }
                .nf-hub-card small {
                    font-size: 11.5px;
                    color: rgba(255, 255, 255, 0.5);
                }

                @keyframes nf-radar {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.3; transform: scale(0.85); }
                }
                @keyframes nf-spin {
                    from { transform: rotate(-25deg); }
                    to { transform: rotate(335deg); }
                }
                @keyframes nf-spin-reverse {
                    from { transform: rotate(20deg); }
                    to { transform: rotate(-340deg); }
                }
                @keyframes nf-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-8px); }
                }
            `}</style>
        </Layout>
    );
};

export default NotFoundPage;
